import type { ReactElement } from 'react';

import type { BadgeTone } from './StatusBadge';

/* A dot and a word, for the four questions this product asks about everything it measures: is it
 * healthy, how bad is it, how sure are we, and what state is it in.
 *
 * **Four named components over one generic `<Indicator tone=…>`**, and that is the design rather than
 * ceremony. A severity and a health are different vocabularies — `critical` is a severity and
 * `degraded` is a health — and a single component taking a free `label` is how a screen ends up
 * rendering "warning" where the API said "partial". Each of these maps a *closed* domain vocabulary
 * onto a tone, so adding a member to the API's enum is a compile error here rather than a row that
 * silently renders in the wrong colour. `StatusBadge` remains the right choice where the vocabulary
 * is the screen's own.
 *
 * **The dot is never the only channel.** Colour carries the same fact the word does, so the component
 * cannot be used to encode something the label omits — which is what the accessibility requirement
 * means in practice, and it is enforced by the label being required rather than optional.
 */

const TONE_VAR: Readonly<Record<BadgeTone, string>> = {
  ok: 'var(--status-ok)',
  info: 'var(--status-info)',
  warn: 'var(--status-warn)',
  danger: 'var(--status-error)',
  unknown: 'var(--status-unknown)',
  neutral: 'var(--text-muted)',
};

export interface IndicatorProps {
  readonly tone: BadgeTone;
  readonly label: string;
  /** Why this state, for the cases a word cannot carry. Rendered as a native tooltip. */
  readonly title?: string | undefined;
}

/** The shared rendering. Exported because the four wrappers below are its whole public surface. */
export function Indicator({ tone, label, title }: IndicatorProps): ReactElement {
  return (
    <span
      className="lq-indicator"
      style={{ ['--indicator-colour' as string]: TONE_VAR[tone] }}
      {...(title === undefined ? {} : { title })}
    >
      {/* Hollow for `unknown`, so an unmeasured state is distinguishable from a measured one with
          the colour ignored entirely. */}
      <span
        className={
          tone === 'unknown'
            ? 'lq-indicator__dot lq-indicator__dot--hollow'
            : 'lq-indicator__dot'
        }
        aria-hidden="true"
      />
      <span className="lq-indicator__label">{label}</span>
    </span>
  );
}

/** Whether a thing is working. `degraded` is `warn` and `unavailable` is `danger`. */
export type Health = 'healthy' | 'degraded' | 'unavailable' | 'unknown';

const HEALTH_TONE = {
  healthy: 'ok',
  degraded: 'warn',
  unavailable: 'danger',
  unknown: 'unknown',
} as const satisfies Record<Health, BadgeTone>;

export function HealthIndicator({
  health,
  label,
  title,
}: {
  readonly health: Health;
  readonly label: string;
  readonly title?: string | undefined;
}): ReactElement {
  return <Indicator tone={HEALTH_TONE[health]} label={label} title={title} />;
}

/** How bad a finding is. The platform's own vocabulary — see `IncidentSeverity` and rule severity. */
export type Severity = 'critical' | 'error' | 'warning' | 'info';

/* `error` and `critical` are both red on purpose. They come from two different enums — incidents use
 * critical/warning/info, rules use blocker/error/warning/info — and a reader should not have to learn
 * which module they are looking at to know that red means act now. */
const SEVERITY_TONE = {
  critical: 'danger',
  error: 'danger',
  warning: 'warn',
  info: 'info',
} as const satisfies Record<Severity, BadgeTone>;

export function SeverityIndicator({
  severity,
  label,
}: {
  readonly severity: Severity;
  readonly label: string;
}): ReactElement {
  return <Indicator tone={SEVERITY_TONE[severity]} label={label} />;
}

/* How much weight a *model's* answer carries. Violet throughout rather than a red-amber-green ramp,
 * because a low-confidence model answer is not a fault — it is an opinion the platform is telling you
 * to weigh, and colouring it red would put it in the queue people work through as breakage. That is
 * the same argument `--status-unknown` exists for. */
export function ConfidenceIndicator({
  label,
  title,
}: {
  readonly label: string;
  readonly title?: string | undefined;
}): ReactElement {
  return <Indicator tone="unknown" label={label} title={title} />;
}
